// <LiveTileVideo> — per-tile WHEP live video for the dashboard wall.
//
// Renders a muted <video> overlaid on the tile. Opens a WHEP session
// when the tile is in the viewport AND the document is visible; tears
// down when either condition flips false. Snapshot underlay (drawn by
// the parent CameraTile) shows through while we connect, reconnect, or
// are gated off.
//
// Cost model:
//  - One RTCPeerConnection per visible tile. AVA enforces a per-device
//    session TTL (~50s); we pre-open a successor at TTL-5s and cut over
//    on track. Audio transceiver is never added (defaultAudio=false).
//  - 16 simultaneous wall tiles ⇒ 16 PeerConnections at peak. To avoid
//    a thundering-herd token-rotation race on first paint, opens are
//    staggered by `STAGGER_MS * deterministicHash(deviceId) mod N`.
//  - Pulled offscreen / hidden tab ⇒ session is closed via
//    `closeSession` after VISIBILITY_TEARDOWN_MS so AVA reclaims the
//    session slot.

import { useEffect, useRef, useState, type RefObject } from "react";
import { liveViewClient } from "@/api/liveViewClient";
import { publishStream } from "./liveStreamRegistry";

export interface LiveTileVideoProps {
  deviceId: string;
  /** When false, no PeerConnection is opened. Used by the parent
   *  CameraTile to skip live for edit-mode / staticTile / offline. */
  enabled: boolean;
  /** From useViewportPriority — open only when "visible". */
  priority: "visible" | "near" | "far";
  /** Forward a ref to the parent tile root (currently unused; reserved
   *  for a future tile-local IntersectionObserver). */
  rootRef: RefObject<HTMLElement>;
}

const STAGGER_MS = 150;
const STAGGER_BUCKETS = 16;
const PRE_OPEN_LEAD_MS = 5_000;
const VISIBILITY_TEARDOWN_MS = 5_000;

interface ActiveSession {
  pc: RTCPeerConnection;
  teardownUrl: string;
  expiresAt: number;
}

/** Deterministic per-device stagger so a wall of tiles doesn't all
 *  POST /live-view/open in the same tick. */
function staggerDelayFor(deviceId: string): number {
  let h = 5381;
  for (let i = 0; i < deviceId.length; i++) {
    h = ((h << 5) + h + deviceId.charCodeAt(i)) | 0;
  }
  return (Math.abs(h) % STAGGER_BUCKETS) * STAGGER_MS;
}

export function LiveTileVideo({
  deviceId,
  enabled,
  priority,
  rootRef,
}: LiveTileVideoProps): JSX.Element | null {
  void rootRef;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [streaming, setStreaming] = useState<boolean>(false);

  // Refs that stay in sync with the latest props/visibility, so the
  // long-lived effect below can read them without re-creating the
  // PeerConnection on every prop change.
  const enabledRef = useRef<boolean>(enabled);
  const priorityRef = useRef<"visible" | "near" | "far">(priority);
  const docVisibleRef = useRef<boolean>(
    typeof document !== "undefined" ? document.visibilityState === "visible" : true,
  );
  enabledRef.current = enabled;
  priorityRef.current = priority;

  // Reconcile signal — bumped by the prop-sync effect to nudge the
  // master effect into re-evaluating its gate.
  const [reconcileTick, setReconcileTick] = useState<number>(0);

  // Bump reconcileTick whenever an external input changes that the
  // master effect should react to.
  useEffect(() => {
    setReconcileTick((n) => n + 1);
  }, [enabled, priority]);

  // Master lifecycle: holds the active session (if any), opens/closes
  // based on the latest gate, and runs rotation timers. Lives across
  // prop changes (deviceId is the only hard reset).
  useEffect(() => {
    let cancelled = false;
    const activeRef: { current: ActiveSession | null } = { current: null };
    const wantsRef = { current: false };
    const timers: { rotation: number | null; start: number | null; teardown: number | null } = {
      rotation: null,
      start: null,
      teardown: null,
    };

    function clearTimer(key: keyof typeof timers): void {
      const v = timers[key];
      if (v !== null) {
        window.clearTimeout(v);
        timers[key] = null;
      }
    }

    async function close(): Promise<void> {
      const cur = activeRef.current;
      activeRef.current = null;
      clearTimer("rotation");
      if (videoRef.current) {
        try {
          videoRef.current.srcObject = null;
        } catch {
          /* ignore */
        }
      }
      setStreaming(false);
      // Drop the registry entry so any live-stream subscribers (modal
      // hero) detach gracefully. Tile and modal both fall back to the
      // snapshot underlay until the next open.
      publishStream(deviceId, null);
      if (cur) {
        try {
          cur.pc.close();
        } catch {
          /* ignore */
        }
        try {
          await liveViewClient.closeSession(cur.teardownUrl);
        } catch {
          /* best effort */
        }
      }
    }

    async function openOnce(): Promise<ActiveSession | null> {
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });
      pc.addTransceiver("video", { direction: "recvonly" });
      // Audio is intentionally NOT added — dashboard tiles are visual-only.

      const remoteStream = new MediaStream();
      pc.addEventListener("track", (e) => {
        remoteStream.addTrack(e.track);
        if (videoRef.current) {
          videoRef.current.srcObject = remoteStream;
          const p = videoRef.current.play();
          if (p) p.catch(() => {});
        }
        // Publish to the shared registry so the focus-modal hero can
        // bind to the same MediaStream without opening a second WHEP
        // session.
        publishStream(deviceId, remoteStream);
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      if (!offer.sdp) {
        pc.close();
        return null;
      }
      try {
        const resp = await liveViewClient.openSession(deviceId, offer.sdp);
        if (cancelled || !wantsRef.current) {
          // We were torn down between offer-post and answer; clean up.
          try {
            pc.close();
          } catch {
            /* ignore */
          }
          try {
            await liveViewClient.closeSession(resp.teardown_url);
          } catch {
            /* ignore */
          }
          return null;
        }
        await pc.setRemoteDescription({ type: "answer", sdp: resp.sdp_answer });
        return {
          pc,
          teardownUrl: resp.teardown_url,
          expiresAt: Date.now() + resp.session_ttl_seconds * 1000,
        };
      } catch {
        try {
          pc.close();
        } catch {
          /* ignore */
        }
        return null;
      }
    }

    function scheduleRotation(active: ActiveSession): void {
      clearTimer("rotation");
      const lead = active.expiresAt - Date.now() - PRE_OPEN_LEAD_MS;
      const delay = Math.max(lead, 1_000);
      timers.rotation = window.setTimeout(() => {
        void rotate();
      }, delay);
    }

    async function rotate(): Promise<void> {
      if (cancelled || !wantsRef.current) return;
      const next = await openOnce();
      if (!next) return;
      const prev = activeRef.current;
      activeRef.current = next;
      setStreaming(true);
      // The new track event will publish the next MediaStream into the
      // registry; that re-renders any subscribed modal hero with the
      // fresh stream so the cutover is invisible.
      scheduleRotation(next);
      if (prev) {
        try {
          prev.pc.close();
        } catch {
          /* ignore */
        }
        try {
          await liveViewClient.closeSession(prev.teardownUrl);
        } catch {
          /* ignore */
        }
      }
    }

    async function start(): Promise<void> {
      if (cancelled || !wantsRef.current) return;
      // First-paint stagger so 16 tiles don't POST /live-view/open in
      // the same tick (otherwise we kick AVA's token-rotation race).
      const delay = staggerDelayFor(deviceId);
      await new Promise<void>((res) => {
        timers.start = window.setTimeout(() => res(), delay);
      });
      if (cancelled || !wantsRef.current) return;
      const next = await openOnce();
      if (!next) return;
      activeRef.current = next;
      setStreaming(true);
      scheduleRotation(next);
    }

    function shouldHoldSession(): boolean {
      return enabledRef.current && priorityRef.current === "visible" && docVisibleRef.current;
    }

    function reconcile(): void {
      const want = shouldHoldSession();
      const prev = wantsRef.current;
      wantsRef.current = want;
      if (want && !prev) {
        clearTimer("teardown");
        if (!activeRef.current) void start();
      } else if (!want && prev) {
        clearTimer("teardown");
        timers.teardown = window.setTimeout(() => {
          if (!wantsRef.current) void close();
        }, VISIBILITY_TEARDOWN_MS);
      }
    }

    function onVisibility(): void {
      docVisibleRef.current = document.visibilityState === "visible";
      reconcile();
    }
    document.addEventListener("visibilitychange", onVisibility);

    function onBeforeUnload(): void {
      const cur = activeRef.current;
      if (cur) {
        try {
          liveViewClient.closeSessionBeacon(cur.teardownUrl);
        } catch {
          /* ignore */
        }
      }
    }
    window.addEventListener("beforeunload", onBeforeUnload);

    // Expose reconcile so the prop-sync effect can poke us via a
    // module-level handler attached to the videoRef target.
    const reconcileHandler = (): void => reconcile();
    const node = videoRef.current;
    if (node) {
      node.addEventListener("__reb_reconcile", reconcileHandler);
    }

    // Initial gate evaluation.
    reconcile();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", onBeforeUnload);
      if (node) {
        node.removeEventListener("__reb_reconcile", reconcileHandler);
      }
      clearTimer("rotation");
      clearTimer("start");
      clearTimer("teardown");
      void close();
    };
  }, [deviceId]);

  // Whenever the master effect should re-evaluate (enabled/priority
  // changed), dispatch a custom event the master listens for.
  useEffect(() => {
    const node = videoRef.current;
    if (!node) return;
    node.dispatchEvent(new CustomEvent("__reb_reconcile"));
  }, [reconcileTick]);

  if (!enabled) return null;
  return (
    <video
      ref={videoRef}
      className={`v7-cam-tile__video${streaming ? " is-streaming" : ""}`}
      playsInline
      muted
      autoPlay
      aria-hidden="true"
    />
  );
}
